import React, { useEffect, useState, useMemo, memo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  FaUsers,
  FaClock,
  FaBox,
  FaArrowTrendUp,
  FaArrowTrendDown,
} from "react-icons/fa6";
import { FaCheckCircle, FaExclamationTriangle, FaBolt } from "react-icons/fa";
import PQueue from "p-queue";
import {
  getTotalUsers,
  getTotalOrders,
  getTotalItems,
  getCompletedOrdersByPeriod,
  getMostCompletedOrdersByPeriod,
  getAverageOrderCompletionTimeByPeriod,
  getOrdersStatuses,
  getOversightsByPeriod,
  getCompareOrdersByDayGrouped,
  getCompareOrdersByYearGrouped,
  getFastestAndSlowestOrdersParsed,
} from "../../api/dashboard";
import "./Dashboard.css";

// Spinner Component
const Spinner = () => (
  <div className="spinner-container">
    <div className="spinner"></div>
  </div>
);

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const RADIAN = Math.PI / 180;

// Memoized PieChart component with customized label
const MemoizedPieChart = memo(({ data }) => {
  const total = useMemo(
    () => data.reduce((sum, entry) => sum + entry.value, 0),
    [data],
  );

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    value,
    index,
  }) => {
    const percent = (value / total) * 100;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize="12"
      >
        {`${name}: ${value} (${percent.toFixed(1)}%)`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={0}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          label={renderCustomizedLabel}
          labelLine={{ stroke: "#ffffff", strokeWidth: 1 }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={`url(#color${index})`} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            background: "rgba(255, 254, 254, 0.57)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "8px",
            color: "#000",
          }}
          formatter={(value, name) => {
            const percentage = ((value / total) * 100).toFixed(1);
            return [`${value} (${percentage}%)`, name];
          }}
        />
        <Legend wrapperStyle={{ color: "#ffffff" }} />
        <defs>
          {data.map((entry, index) => (
            <linearGradient
              key={`color${index}`}
              id={`color${index}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor={COLORS[index % COLORS.length]}
                stopOpacity={0.9}
              />
              <stop
                offset="95%"
                stopColor={COLORS[index % COLORS.length]}
                stopOpacity={0.1}
              />
            </linearGradient>
          ))}
        </defs>
      </PieChart>
    </ResponsiveContainer>
  );
});

// Memoized AreaChart component
const MemoizedAreaChart = memo(({ dataThisYear, dataLastYear, title }) => {
  const getPeriodValue = (period) => {
    if (title === "Monthly") {
      return parseInt(period);
    } else {
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return monthNames.indexOf(period) + 1;
    }
  };

  const sortedThisYear = useMemo(
    () =>
      [...dataThisYear].sort(
        (a, b) => getPeriodValue(a.period) - getPeriodValue(b.period),
      ),
    [dataThisYear, title],
  );

  const sortedLastYear = useMemo(
    () =>
      [...dataLastYear].sort(
        (a, b) => getPeriodValue(a.period) - getPeriodValue(b.period),
      ),
    [dataLastYear, title],
  );

  // Merge data from both years into a single dataset
  const mergedData = useMemo(() => {
    const lastYearMap = {};
    sortedLastYear.forEach((item) => {
      lastYearMap[item.period] = item;
    });

    return sortedThisYear.map((item) => ({
      period: item.period,
      orderCountThisYear: item.orderCount || 0,
      oversightCountThisYear: item.oversightCount || 0,
      orderCountLastYear: lastYearMap[item.period]?.orderCount || 0,
      oversightCountLastYear: lastYearMap[item.period]?.oversightCount || 0,
    }));
  }, [sortedThisYear, sortedLastYear]);

  const maxThisYearOrders =
    mergedData.length > 0
      ? Math.max(...mergedData.map((item) => item.orderCountThisYear))
      : 0;
  const maxThisYearOversights =
    mergedData.length > 0
      ? Math.max(...mergedData.map((item) => item.oversightCountThisYear))
      : 0;
  const maxLastYearOrders =
    mergedData.length > 0
      ? Math.max(...mergedData.map((item) => item.orderCountLastYear))
      : 0;
  const maxLastYearOversights =
    mergedData.length > 0
      ? Math.max(...mergedData.map((item) => item.oversightCountLastYear))
      : 0;
  const maxValue = Math.max(
    maxThisYearOrders,
    maxThisYearOversights,
    maxLastYearOrders,
    maxLastYearOversights,
    25,
  );

  const maxTicks = title === "Monthly" ? 31 : 12;
  const tickCount = Math.min(maxTicks, mergedData.length);
  const ticks = useMemo(
    () =>
      Array.from(
        { length: tickCount },
        (_, i) => Math.floor((i * mergedData.length) / tickCount) + 1,
      ).filter((tick, index, self) => self.indexOf(tick) === index),
    [mergedData.length, tickCount],
  );

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart
        data={mergedData}
        margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="rgba(255, 255, 255, 0.1)"
        />
        <XAxis
          dataKey="period"
          stroke="#ffffff"
          fontSize={12}
          interval={Math.floor(mergedData.length / tickCount) || 0}
          ticks={ticks}
          tickFormatter={(value) =>
            title === "Monthly"
              ? value
              : monthNames[parseInt(value) - 1] || value
          }
        />
        <YAxis
          stroke="#ffffff"
          fontSize={12}
          domain={[0, maxValue]}
          allowDataOverflow={false}
          tickCount={Math.ceil(maxValue / 5) + 1}
        />
        <Tooltip
          contentStyle={{
            background: "rgba(0, 0, 0, 0.8)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "8px",
            color: "#ffffff",
          }}
          formatter={(value, name) => [value, name]}
          labelFormatter={(label) =>
            title === "Monthly"
              ? label
              : monthNames[parseInt(label) - 1] || label
          }
        />
        <Legend />
        <Area
          type="monotone"
          dataKey="orderCountThisYear"
          name="Orders This Year"
          stroke="#3b82f6"
          fill="url(#thisYearGradient)"
          fillOpacity={0.4}
          strokeWidth={2}
        />
        <Area
          type="monotone"
          dataKey="oversightCountThisYear"
          name="Oversights This Year"
          stroke="#ef4444"
          fill="url(#thisYearOversightGradient)"
          fillOpacity={0.4}
          strokeWidth={2}
        />
        {sortedLastYear.length > 0 && (
          <>
            <Area
              type="monotone"
              dataKey="orderCountLastYear"
              name="Orders Last Year"
              stroke="#10b981"
              fill="url(#lastYearGradient)"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="oversightCountLastYear"
              name="Oversights Last Year"
              stroke="#f97316"
              fill="url(#lastYearOversightGradient)"
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </>
        )}
        <defs>
          <linearGradient id="thisYearGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient
            id="thisYearOversightGradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id="lastYearGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient
            id="lastYearOversightGradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="5%" stopColor="#f97316" stopOpacity={0.5} />
            <stop offset="95%" stopColor="#f97316" stopOpacity={0.1} />
          </linearGradient>
        </defs>
      </AreaChart>
    </ResponsiveContainer>
  );
});

const Dashboard = () => {
  const [dailyData, setDailyData] = useState({
    ordersStatuses: [],
    topAgents: [],
    totalUsers: 0,
    totalOrders: 0,
    unfinishedOrdersBeforeToday: 0,
    totalItems: 0,
    totalCompletedOrders: 0,
    avgTime: "00:00:00",
    avgTimeMonth: "00:00:00",
    avgTimeYear: "00:00:00",
    oversights: [],
    oversightsMonth: 0,
    oversightsYear: 0,
    fastestOrder: [],
  });
  const [monthlyData, setMonthlyData] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [dailyError, setDailyError] = useState(null);
  const [monthlyError, setMonthlyError] = useState(null);
  const [annualError, setAnnualError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);

  // Initialize PQueue with concurrency of 2
  const queue = new PQueue({ concurrency: 2 });

  const formatTimeForDaily = (timeStr) => {
    if (timeStr === "N/A" || !timeStr) return "N/A";
    const [h, m, s] = timeStr.split(":").map(Number);
    const totalSeconds = h * 3600 + m * 60 + s;
    return Math.round(totalSeconds / 60).toString();
  };

  const formatTime = (timeStr) => {
    if (timeStr === "N/A" || !timeStr) return "N/A";
    const [h, m, s] = timeStr.split(":").map(Number);
    const totalSeconds = h * 3600 + m * 60 + s;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const fetchInitialData = async () => {
    setDailyError(null);
    setIsAuthError(false);
    try {
      // Use PQueue to limit concurrency
      const [
        usersRes,
        ordersRes,
        itemsRes,
        completedOrdersRes,
        topAgentsRes,
        avgTimeRes,
        statusesRes,
        oversightsRes,
        fastestSlowestRes,
      ] = await Promise.all([
        queue.add(() => getTotalUsers()),
        queue.add(() => getTotalOrders()),
        queue.add(() => getTotalItems()),
        queue.add(() => getCompletedOrdersByPeriod()),
        queue.add(() => getMostCompletedOrdersByPeriod()),
        queue.add(() => getAverageOrderCompletionTimeByPeriod()),
        queue.add(() => getOrdersStatuses()),
        queue.add(() => getOversightsByPeriod()),
        queue.add(() => getFastestAndSlowestOrdersParsed()),
      ]);

      // Check for auth errors (401/403)
      const responses = [
        { res: usersRes, name: "getTotalUsers" },
        { res: ordersRes, name: "getTotalOrders" },
        { res: itemsRes, name: "getTotalItems" },
        { res: completedOrdersRes, name: "getCompletedOrdersByPeriod" },
        { res: topAgentsRes, name: "getMostCompletedOrdersByPeriod" },
        { res: avgTimeRes, name: "getAverageOrderCompletionTimeByPeriod" },
        { res: statusesRes, name: "getOrdersStatuses" },
        { res: oversightsRes, name: "getOversightsByPeriod" },
        { res: fastestSlowestRes, name: "getFastestAndSlowestOrdersParsed" },
      ];

      const hasAuthError = responses.some(
        ({ res }) =>
          res.isAxiosError && [401, 403].includes(res.response?.status),
      );
      if (hasAuthError) {
        setIsAuthError(true);
        setDailyError("Authentication error");
        return;
      }

      // Log failed endpoints
      const failedEndpoints = responses
        .filter(({ res }) => res.isAxiosError)
        .map(({ res, name }) => ({
          endpoint: name,
          status: res.response?.status,
          message: res.response?.data?.error || res.message,
        }));
      if (failedEndpoints.length > 0) {
        console.error("Failed API calls in fetchInitialData:", failedEndpoints);
        setDailyError("Failed to load daily data");
        return;
      }

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().toLocaleString("default", {
        month: "long",
      });
      const currentDay = new Date().getDate();

      const ordersStatuses = Object.entries(
        statusesRes.ordersStatuses || {},
      ).map(([name, value]) => ({
        name,
        value,
      }));

      const dailyTopAgents = (topAgentsRes.daily || [])
        .slice(0, 3)
        .map(
          (agent) =>
            `${agent.userId} (${agent.headerCount} orders, ${agent.salesLinesCount} items)`,
        );

      const todayAvg =
        avgTimeRes.averageCompletionTime?.daily?.[currentYear]?.find(
          (d) => parseInt(d.day) === currentDay,
        )?.count || "00:00:00";

      const monthAvg =
        avgTimeRes.averageCompletionTime?.monthly?.[currentYear]?.[
          currentMonth
        ] || "00:00:00";

      const yearAvg =
        avgTimeRes.averageCompletionTime?.yearly?.[currentYear] || "00:00:00";

      const dailyOversights = oversightsRes.daily?.[currentYear.toString()]
        ?.filter((item) => parseInt(item.day) === currentDay)
        .map((item) => `${item.count}`) || ["No oversights today"];

      const monthlyOversights =
        oversightsRes.monthly?.[currentYear.toString()]?.[currentMonth] || 0;

      const yearlyOversights =
        oversightsRes.yearly?.[currentYear.toString()] || 0;

      const fastestOrder = [
        `Order: ${fastestSlowestRes.fastest?.orderNo || "N/A"}`,
        `Agent: ${fastestSlowestRes.fastest?.userId || "N/A"}`,
        `Time Sec: ${fastestSlowestRes.fastest?.timeSeconds || "N/A"}`,
      ];

      setDailyData({
        ordersStatuses,
        topAgents: dailyTopAgents,
        totalUsers: usersRes.totalUsers || 0,
        totalOrders: ordersRes.totalOrders || 0,
        unfinishedOrdersBeforeToday: ordersRes.unfinishedOrdersBeforeToday || 0,
        totalItems: itemsRes.totalItems || 0,
        totalCompletedOrders: completedOrdersRes.daily || 0,
        avgTime: todayAvg,
        avgTimeMonth: monthAvg,
        avgTimeYear: yearAvg,
        oversights: dailyOversights,
        oversightsMonth: monthlyOversights,
        oversightsYear: yearlyOversights,
        fastestOrder,
      });
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setDailyError("Failed to load daily data");
    }
  };

  const fetchMonthlyData = async () => {
    if (monthlyData) return;
    setMonthlyError(null);
    try {
      // Use PQueue to limit concurrency
      const [comparisonRes, topAgentsRes, avgTimeRes, oversightsRes] =
        await Promise.all([
          queue.add(() => getCompareOrdersByDayGrouped()),
          queue.add(() => getMostCompletedOrdersByPeriod()),
          queue.add(() => getAverageOrderCompletionTimeByPeriod()),
          queue.add(() => getOversightsByPeriod()),
        ]);

      // Check for auth errors (401/403)
      const responses = [
        { res: comparisonRes, name: "getCompareOrdersByDayGrouped" },
        { res: topAgentsRes, name: "getMostCompletedOrdersByPeriod" },
        { res: avgTimeRes, name: "getAverageOrderCompletionTimeByPeriod" },
        { res: oversightsRes, name: "getOversightsByPeriod" },
      ];

      const hasAuthError = responses.some(
        ({ res }) =>
          res.isAxiosError && [401, 403].includes(res.response?.status),
      );
      if (hasAuthError) {
        setIsAuthError(true);
        setMonthlyError("Authentication error");
        return;
      }

      // Log failed endpoints
      const failedEndpoints = responses
        .filter(({ res }) => res.isAxiosError)
        .map(({ res, name }) => ({
          endpoint: name,
          status: res.response?.status,
          message: res.response?.data?.error || res.message,
        }));
      if (failedEndpoints.length > 0) {
        console.error("Failed API calls in fetchMonthlyData:", failedEndpoints);
        setMonthlyError("Failed to load monthly data");
        return;
      }

      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      const currentMonth = new Date().toLocaleString("default", {
        month: "long",
      });
      const currentMonthNumber = new Date().getMonth() + 1;

      const thisYearData = comparisonRes[currentYear] || [];
      const lastYearData = comparisonRes[lastYear] || [];

      const thisYearMonthData = thisYearData.filter((item) => {
        const itemDate = new Date(
          `${currentMonth} ${item.day}, ${currentYear}`,
        );
        return itemDate.getMonth() + 1 === currentMonthNumber;
      });

      const lastYearMonthData = lastYearData.filter((item) => {
        const itemDate = new Date(`${currentMonth} ${item.day}, ${lastYear}`);
        return itemDate.getMonth() + 1 === currentMonthNumber;
      });

      const thisYearChartData = thisYearMonthData.map((item) => ({
        period: item.day.toString(),
        orderCount: item.count || 0,
        oversightCount:
          oversightsRes.daily?.[currentYear.toString()]?.find(
            (o) => parseInt(o.day) === parseInt(item.day),
          )?.count || 0,
      }));

      const lastYearChartData = lastYearMonthData.map((item) => ({
        period: item.day.toString(),
        orderCount: item.count || 0,
        oversightCount:
          oversightsRes.daily?.[lastYear.toString()]?.find(
            (o) => parseInt(o.day) === parseInt(item.day),
          )?.count || 0,
      }));

      const totalCompletedOrders = thisYearMonthData.reduce(
        (sum, item) => sum + (item.count || 0),
        0,
      );
      const lastYearCompletedOrders = lastYearMonthData.reduce(
        (sum, item) => sum + (item.count || 0),
        0,
      );

      const monthlyTopAgents = (topAgentsRes.monthly || [])
        .slice(0, 2)
        .map(
          (agent) =>
            `${agent.userId} (${agent.headerCount} orders, ${agent.salesLinesCount} items)`,
        );

      const currentYearStr = currentYear.toString();
      const thisMonthAvg =
        avgTimeRes.averageCompletionTime?.monthly?.[currentYearStr]?.[
          currentMonth
        ] || "00:00:00";
      const lastYearMonthAvg =
        avgTimeRes.averageCompletionTime?.monthly?.[lastYear]?.[currentMonth] ||
        "N/A";

      const monthlyOversightsThisYear =
        oversightsRes.monthly?.[currentYearStr]?.[currentMonth] || 0;

      const monthlyOversightsLastYear =
        oversightsRes.monthly?.[lastYear.toString()]?.[currentMonth] || 0;

      const oversightsComparison = [
        `CM: ${monthlyOversightsThisYear} oversights`,
        `PY: ${monthlyOversightsLastYear} oversights`,
      ];

      setMonthlyData({
        comparison: {
          thisYear: thisYearChartData,
          lastYear: lastYearChartData,
        },
        totalCompletedOrders,
        lastYearCompletedOrders,
        topAgent: monthlyTopAgents,
        avgTime: thisMonthAvg,
        lastYearAvgTime: lastYearMonthAvg,
        oversights: oversightsComparison,
      });
    } catch (err) {
      console.error("Error fetching monthly data:", err);
      setMonthlyError("Failed to load monthly data");
    }
  };

  const fetchAnnualData = async () => {
    if (annualData) return;
    setAnnualError(null);
    try {
      // Use PQueue to limit concurrency
      const [comparisonRes, topAgentsRes, avgTimeRes, oversightsRes] =
        await Promise.all([
          queue.add(() => getCompareOrdersByYearGrouped()),
          queue.add(() => getMostCompletedOrdersByPeriod()),
          queue.add(() => getAverageOrderCompletionTimeByPeriod()),
          queue.add(() => getOversightsByPeriod()),
        ]);

      // Check for auth errors (401/403)
      const responses = [
        { res: comparisonRes, name: "getCompareOrdersByYearGrouped" },
        { res: topAgentsRes, name: "getMostCompletedOrdersByPeriod" },
        { res: avgTimeRes, name: "getAverageOrderCompletionTimeByPeriod" },
        { res: oversightsRes, name: "getOversightsByPeriod" },
      ];

      const hasAuthError = responses.some(
        ({ res }) =>
          res.isAxiosError && [401, 403].includes(res.response?.status),
      );
      if (hasAuthError) {
        setIsAuthError(true);
        setAnnualError("Authentication error");
        return;
      }

      // Log failed endpoints
      const failedEndpoints = responses
        .filter(({ res }) => res.isAxiosError)
        .map(({ res, name }) => ({
          endpoint: name,
          status: res.response?.status,
          message: res.response?.data?.error || res.message,
        }));
      if (failedEndpoints.length > 0) {
        console.error("Failed API calls in fetchAnnualData:", failedEndpoints);
        setAnnualError("Failed to load annual data");
        return;
      }

      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;

      const thisYearData = comparisonRes[currentYear] || [];
      const lastYearData = comparisonRes[lastYear] || [];

      const totalCompletedOrders = thisYearData.reduce(
        (sum, item) => sum + (item.count || 0),
        0,
      );
      const lastYearCompletedOrders = lastYearData.reduce(
        (sum, item) => sum + (item.count || 0),
        0,
      );

      const currentYearStr = currentYear.toString();
      const thisYearMonthlyOversights =
        oversightsRes.monthly?.[currentYearStr] || {};

      const thisYearChartData = thisYearData.map((item) => ({
        period: item.month || "",
        orderCount: item.count || 0,
        oversightCount: thisYearMonthlyOversights[item.month] || 0,
      }));
      const lastYearMonthlyOversights =
        oversightsRes.monthly?.[lastYear.toString()] || {};
      const lastYearChartData = lastYearData.map((item) => ({
        period: item.month || "",
        orderCount: item.count || 0,
        oversightCount: lastYearMonthlyOversights[item.month] || 0,
      }));

      const yearlyTopAgents = (topAgentsRes.yearly || [])
        .slice(0, 2)
        .map(
          (agent) =>
            `${agent.userId} (${agent.headerCount} orders, ${agent.salesLinesCount} items)`,
        );

      const thisYearAvg =
        avgTimeRes.averageCompletionTime?.yearly?.[currentYear] || "00:00:00";
      const lastYearAvg =
        avgTimeRes.averageCompletionTime?.yearly?.[lastYear] || "N/A";

      const yearlyOversights = Object.entries(oversightsRes.yearly || {})
        .filter(([year]) => parseInt(year) >= currentYear - 1)
        .map(([year, count]) => `${year}: ${count} oversights`);

      setAnnualData({
        comparison: {
          thisYear: thisYearChartData,
          lastYear: lastYearChartData,
        },
        totalCompletedOrders,
        lastYearCompletedOrders,
        topAgent: yearlyTopAgents,
        avgTime: thisYearAvg,
        lastYearAvgTime: lastYearAvg,
        oversights: yearlyOversights.length
          ? yearlyOversights
          : ["No oversights this year"],
      });
    } catch (err) {
      console.error("Error fetching annual data:", err);
      setAnnualError("Failed to load annual data");
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const monthlySection = document.querySelector("#monthly-section");
    const annualSection = document.querySelector("#annual-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.id === "monthly-section") fetchMonthlyData();
            if (entry.target.id === "annual-section") fetchAnnualData();
          }
        });
      },
      { threshold: 0.1 },
    );

    if (monthlySection) observer.observe(monthlySection);
    if (annualSection) observer.observe(annualSection);

    return () => {
      if (monthlySection) observer.unobserve(monthlySection);
      if (annualSection) observer.unobserve(annualSection);
    };
  }, []);

  const renderMetrics = useMemo(
    () =>
      (
        topAgent,
        totalCompletedOrders,
        avgTime,
        oversights,
        isMonthly = false,
        isAnnual = false,
        lastYearCompletedOrders = 0,
        lastYearAvgTime = "N/A",
      ) => {
        const cmAvgMin = formatTime(avgTime);
        const pyAvgMin = formatTime(lastYearAvgTime);

        const cmAvgNum = parseFloat(cmAvgMin.split(":")[0]) || 0;
        const pyAvgNum = parseFloat(pyAvgMin.split(":")[0]) || 0;

        return (
          <div className="metrics-container">
            <div className="metric-card">
              <h3>
                <FaUsers className="metric-icon" /> Top{" "}
                {isAnnual ? "Agents" : isMonthly ? "Agents" : "Agents"}
              </h3>
              <ul>
                {topAgent.length ? (
                  topAgent.map((agent, index) => (
                    <li key={index} className="top-agent">
                      {agent}
                    </li>
                  ))
                ) : (
                  <li>No agents available</li>
                )}
              </ul>
            </div>
            <div className="metric-card">
              <h3>
                <FaCheckCircle className="metric-icon" /> Completed Orders
              </h3>
              {!(isMonthly || isAnnual) ? (
                <p className="metric-value">{totalCompletedOrders}</p>
              ) : (
                <div className="metric-comparison">
                  <div className="metric-item">
                    <span className="metric-header">
                      {isAnnual ? "CY" : "CM"}
                    </span>
                    <span className="metric-subvalue">
                      {totalCompletedOrders}
                      {totalCompletedOrders > lastYearCompletedOrders ? (
                        <FaArrowTrendUp className="arrow-icon arrow-up" />
                      ) : totalCompletedOrders < lastYearCompletedOrders ? (
                        <FaArrowTrendDown className="arrow-icon arrow-down" />
                      ) : null}
                    </span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-header">PY</span>
                    <span className="metric-subvalue">
                      {lastYearCompletedOrders}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="metric-card">
              <h3>
                <FaClock className="metric-icon" /> Avg Time Min
              </h3>
              {!(isMonthly || isAnnual) ? (
                <p className="metric-value">{cmAvgMin}</p>
              ) : (
                <div className="metric-comparison">
                  <div className="metric-item">
                    <span className="metric-header">
                      {isAnnual ? "CY" : "CM"}
                    </span>
                    <span className="metric-subvalue">
                      {cmAvgMin}
                      {cmAvgMin !== "N/A" && pyAvgMin !== "N/A" ? (
                        cmAvgNum < pyAvgNum ? (
                          <FaArrowTrendUp className="arrow-icon arrow-up" />
                        ) : cmAvgNum > pyAvgNum ? (
                          <FaArrowTrendDown className="arrow-icon arrow-down" />
                        ) : null
                      ) : null}
                    </span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-header">PY</span>
                    <span className="metric-subvalue">{pyAvgMin}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="metric-card">
              <h3>
                <FaExclamationTriangle className="metric-icon" /> Oversights
              </h3>
              <ul>
                {oversights.length ? (
                  oversights.map((oversight, index) => (
                    <li key={index} className="oversight-item">
                      {oversight}
                    </li>
                  ))
                ) : (
                  <li>No oversights</li>
                )}
              </ul>
            </div>
          </div>
        );
      },
    [],
  );

  return (
    <div className="dashboard">
      <h1 className="dashboard-title">Warehouse Management Dashboard</h1>

      {/* Daily Overview */}
      <section className="overview-section">
        <h2>Daily Overview</h2>
        <div className="overview-content">
          {dailyError ? (
            <Spinner />
          ) : (
            <>
              <div className="chart-container">
                <h3 className="chart-title">Order Statuses</h3>
                <MemoizedPieChart
                  data={[
                    ...dailyData.ordersStatuses,
                    {
                      name: "Backlog",
                      value: dailyData.unfinishedOrdersBeforeToday,
                    },
                  ]}
                />
              </div>
              <div className="metrics-container">
                <div className="metric-card">
                  <h3>
                    <FaUsers className="metric-icon" /> Top Agents
                  </h3>
                  <ul>
                    {dailyData.topAgents.length ? (
                      dailyData.topAgents.map((agent, index) => (
                        <li key={index} className="top-agent">
                          {agent}
                        </li>
                      ))
                    ) : (
                      <li>No agents available</li>
                    )}
                  </ul>
                </div>
                <div className="metric-card">
                  <h3>
                    <FaBolt className="metric-icon" /> Fastest Order
                  </h3>
                  <ul>
                    {dailyData.fastestOrder.length ? (
                      dailyData.fastestOrder.map((order, index) => (
                        <li key={index} className="top-agent">
                          {order}
                        </li>
                      ))
                    ) : (
                      <li>No data available</li>
                    )}
                  </ul>
                </div>
                <div className="metric-card">
                  <h3>
                    <FaCheckCircle className="metric-icon" /> Completed
                  </h3>
                  <p className="metric-value">
                    {dailyData.totalCompletedOrders}
                  </p>
                </div>
                <div className="metric-card">
                  <h3>
                    <FaBox className="metric-icon" /> Backlog
                  </h3>
                  <p className="metric-value">
                    {dailyData.unfinishedOrdersBeforeToday}
                  </p>
                </div>
                <div className="metric-card">
                  <h3>
                    <FaClock className="metric-icon" /> Avg Time Min
                  </h3>
                  <div className="metric-comparison">
                    <div className="metric-item">
                      <span className="metric-header">CD</span>
                      <span className="metric-subvalue">
                        {formatTimeForDaily(dailyData.avgTime)}
                      </span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-header">CM</span>
                      <span className="metric-subvalue">
                        {formatTimeForDaily(dailyData.avgTimeMonth)}
                      </span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-header">CY</span>
                      <span className="metric-subvalue">
                        {formatTimeForDaily(dailyData.avgTimeYear)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="metric-card">
                  <h3>
                    <FaExclamationTriangle className="metric-icon" /> Oversights
                  </h3>
                  <div className="metric-comparison">
                    <div className="metric-item">
                      <span className="metric-header">CD</span>
                      <span className="metric-subvalue">
                        {dailyData.oversights[0] === "No oversights today"
                          ? 0
                          : dailyData.oversights[0]}
                      </span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-header">CM</span>
                      <span className="metric-subvalue">
                        {dailyData.oversightsMonth}
                      </span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-header">CY</span>
                      <span className="metric-subvalue">
                        {dailyData.oversightsYear}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Monthly Overview */}
      <section id="monthly-section" className="overview-section">
        <h2>Monthly Overview</h2>
        <div className="overview-content">
          {monthlyError ? (
            <Spinner />
          ) : monthlyData ? (
            <>
              <div className="chart-container">
                <h3 className="chart-title">Orders This Month vs Last Year</h3>
                <MemoizedAreaChart
                  dataThisYear={monthlyData.comparison.thisYear}
                  dataLastYear={monthlyData.comparison.lastYear}
                  title="Monthly"
                />
              </div>
              {renderMetrics(
                monthlyData.topAgent,
                monthlyData.totalCompletedOrders,
                monthlyData.avgTime,
                monthlyData.oversights,
                true,
                false,
                monthlyData.lastYearCompletedOrders,
                monthlyData.lastYearAvgTime,
              )}
            </>
          ) : (
            <Spinner />
          )}
        </div>
      </section>

      {/* Annual Overview */}
      <section id="annual-section" className="overview-section">
        <h2>Annual Overview</h2>
        <div className="overview-content">
          {annualError ? (
            <Spinner />
          ) : annualData ? (
            <>
              <div className="chart-container">
                <h3 className="chart-title">Orders This Year vs Last Year</h3>
                <MemoizedAreaChart
                  dataThisYear={annualData.comparison.thisYear}
                  dataLastYear={annualData.comparison.lastYear}
                  title="Annual"
                />
              </div>
              {renderMetrics(
                annualData.topAgent,
                annualData.totalCompletedOrders,
                annualData.avgTime,
                annualData.oversights,
                false,
                true,
                annualData.lastYearCompletedOrders,
                annualData.lastYearAvgTime,
              )}
            </>
          ) : (
            <Spinner />
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
